import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Genies.css';
import './InsightsPage.css';
import { useGenies } from './GenieContext';
import api from '../api';

function InsightsPage() {
  const context = useGenies();
  const selectedClient = context?.selectedClient || null;
  const [accessibleProducts, setAccessibleProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAccessibleDashboards = async () => {
      if (!selectedClient) {
        setAccessibleProducts([]);
        setIsLoading(false);
        return;
      }

      try {
        // ── Power BI dashboards ────────────────────────────────────────────
        // Use non-admin endpoints so viewer role can also access dashboards
        const accessResponse = await api.get(
          `/api/powerbi/client-access?client=${encodeURIComponent(selectedClient)}`
        );
        const hasPowerBIAccess = accessResponse.data.hasPowerBIAccess;
        const assignedIds = accessResponse.data.dashboardIds || [];

        const products = [];

        if (hasPowerBIAccess && assignedIds.length > 0) {
          // Fetch dashboard metadata using the non-admin endpoint (isAuthenticated only)
          // GET /api/powerbi/dashboards returns the user's accessible dashboards with full metadata
          try {
            const allPBIRes = await api.get('/api/powerbi/dashboards');
            const allPBIDashboards = (allPBIRes.data && allPBIRes.data.dashboards) ? allPBIRes.data.dashboards : [];
            // Exclude databricks-type dashboards — they are rendered separately in the Databricks section below
            const assignedPBI = allPBIDashboards.filter(d => d.type !== 'databricks' && assignedIds.includes(d.id));

            assignedPBI.forEach(d => {
              products.push({
                id: d.id,
                dashboardId: d.id,
                name: d.name,
                subtitle: 'Power BI',
                description: d.description || 'Interactive Power BI analytics dashboard',
                link: `/powerbi-dashboard?id=${encodeURIComponent(d.id)}&name=${encodeURIComponent(d.name)}`,
                type: 'powerbi',
                beta: d.beta || false
              });
            });
          } catch (pbiErr) {
            console.warn('[InsightsPage] Could not fetch Power BI dashboard metadata:', pbiErr.message);
          }
        }

        // ── Databricks AI/BI dashboards ────────────────────────────────────
        try {
          const dbRes = await api.get('/api/databricks-embed/config');
          if (dbRes.data.success) {
            const dbDashboards = dbRes.data.dashboards || [];
            dbDashboards.forEach(d => {
              products.push({
                id: d.id,
                dashboardId: d.id,
                name: d.name,
                subtitle: 'Databricks AI/BI',
                description: d.description || 'Interactive Databricks AI/BI analytics dashboard',
                link: `/databricks-dashboard?id=${encodeURIComponent(d.id)}&name=${encodeURIComponent(d.name)}`,
                type: 'databricks',
                beta: false
              });
            });
          }
        } catch (dbErr) {
          // Non-fatal — if Databricks dashboards fail we still show Power BI ones
          console.warn('[InsightsPage] Could not fetch Databricks dashboards:', dbErr.message);
        }

        setAccessibleProducts(products);
      } catch (error) {
        console.error('[InsightsPage] Error fetching accessible dashboards:', error);
        setAccessibleProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccessibleDashboards();
  }, [selectedClient]);

  return (
    <>
      <div className="genies-background" />
      <div className="genies-container insights-page" style={{ textAlign: 'left' }}>

        {/* Page Header */}
        <div className="insights-page-header">
          <h1 className="insights-page-title">Data Studio</h1>
          <p className="insights-page-subtitle">Explore analytics, dashboards and AI-powered insights</p>
        </div>

        {/* Insights Sub-section */}
        <div className="insights-section">
          <div className="insights-section-header">
            <h2 className="insights-section-title">Insights</h2>
          </div>

          {isLoading ? (
            <div className="insights-loading">Loading dashboards...</div>
          ) : accessibleProducts.length === 0 ? (
            <div className="insights-empty">No dashboards available.</div>
          ) : (
            <div className="insights-products-grid">
              {accessibleProducts.map((product) => (
                <Link key={product.id} to={product.link} className="insights-product-tile">
                  <div className="insights-product-content">
                    <div className="insights-product-subtitle-row">
                      <span className="insights-product-subtitle">{product.subtitle}</span>
                      {product.type === 'databricks' && (
                        <span className="insights-databricks-badge">AI/BI</span>
                      )}
                    </div>
                    <div className="insights-product-name-row">
                      <h3 className="insights-product-name">{product.name}</h3>
                      {product.beta && <span className="insights-beta-badge">Beta</span>}
                    </div>
                    <p className="insights-product-description">{product.description}</p>
                  </div>
                  <div className="insights-product-footer">
                    <span className="insights-launch-btn">Launch</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

      </div>
    </>
  );
}

export default InsightsPage;
